import {
  BarChart3,
  Building2,
  FileText,
  Home,
  LogOut,
  Menu,
  Moon,
  Sun,
  User,
  X,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { logout } from "@/redux/auth/authSlice";

const BusinessLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem("theme");
    if (saved) return saved === "dark";
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get user role and business type for dynamic navigation
  const businessType =
    user?.role === "RestaurantOwner"
      ? "restaurant"
      : user?.role === "FleetOwner"
        ? "fleet"
        : "courier";

  useEffect(() => {
    const checkIsMobile = () => {
      const width = window.innerWidth;
      setIsMobile(width < 1024); // Use lg breakpoint (1024px) to include tablets
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setProfileDropdownOpen(false);
      }
    };

    if (profileDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileDropdownOpen]);

  // Theme toggle functionality
  useEffect(() => {
    const root = window.document.documentElement;
    if (isDarkMode) {
      root.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      root.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    (async () => {
      await import("@/services/authService").then(({ authService }) =>
        authService.logout(),
      );
      dispatch(logout());
      navigate("/auth/login");
      setShowLogoutConfirm(false);
    })();
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const navigation = [
    {
      href: "/business/dashboard",
      icon: <Home className="h-5 w-5" />,
      name: "Dashboard",
    },
    {
      href: "/business/analytics",
      icon: <BarChart3 className="h-5 w-5" />,
      name: "Analytics",
    },
    {
      href: "/business/profile",
      icon: <User className="h-5 w-5" />,
      name: "Profile",
    },
    {
      href: "/business/management",
      icon: <Building2 className="h-5 w-5" />,
      name: "Management",
    },
    {
      href: "/business/documents",
      icon: <FileText className="h-5 w-5" />,
      name: "Business Information",
    },
  ];

  function getManagementLabel(type: string): string {
    switch (type) {
      case "courier":
        return "Courier service management";
      case "fleet":
        return "Vehicle fleet management";
      case "restaurant":
        return "Menu and order management";
      default:
        return "Business operations";
    }
  }

  const businessTypeLabels = {
    courier: "Courier Service",
    fleet: "Fleet Owner",
    restaurant: "Restaurant Owner",
  };

  const isCurrentPath = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && isMobile && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="absolute inset-0 bg-gray-600 opacity-75"></div>
        </div>
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 bg-white shadow-lg transition-transform duration-300 ease-in-out dark:bg-gray-800 ${
          isMobile
            ? `w-64 transform ${
                sidebarOpen ? "translate-x-0" : "-translate-x-full"
              }`
            : "w-64"
        }`}
      >
        <div className="flex h-16 items-center justify-between bg-blue-600 px-4 dark:bg-blue-700 md:px-6">
          <Link className="flex items-center" to="/">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white dark:bg-gray-800 md:h-10 md:w-10">
              <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400 md:h-6 md:w-6" />
            </div>
            <span className="ml-2 text-xl font-bold text-white dark:text-gray-100 md:text-2xl">
              Hareeng
            </span>
          </Link>
          {isMobile && (
            <button
              className="text-white hover:text-gray-200"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="mt-8">
          <div className="px-4">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              Business Portal
            </p>
          </div>
          {navigation.map((item) => (
            <Link
              className={`flex items-center px-4 py-3 text-sm font-medium transition-colors duration-150 md:px-6 md:py-4 md:text-base ${
                isCurrentPath(item.href)
                  ? "border-r-3 border-blue-600 bg-blue-50 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                  : "text-gray-700 hover:bg-gray-50 hover:text-blue-600 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-blue-400"
              }`}
              key={item.name}
              onClick={() => setSidebarOpen(false)}
              to={item.href}
            >
              <span
                className={`mr-3 md:mr-4 ${isCurrentPath(item.href) ? "text-blue-600 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"}`}
              >
                {item.icon}
              </span>
              {item.name}
            </Link>
          ))}
        </nav>

        {/* User info at bottom */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-200 p-4 dark:border-gray-700 md:p-6">
          <div className="flex items-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900 md:h-12 md:w-12">
              <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-3 md:ml-4">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 md:text-base">
                {user?.firstName
                  ? `${user.firstName} ${user.lastName}`.trim()
                  : "Business Owner"}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 md:text-sm">
                {businessTypeLabels[businessType]}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={isMobile ? "" : "md:pl-64"}>
        {/* Top navbar */}
        <div className="sticky top-0 z-30 border-b border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 md:px-8">
            {/* Mobile menu button */}
            {isMobile && (
              <button
                className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-400"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-6 w-6" />
              </button>
            )}

            {/* Page title */}
            <div className="min-w-0 flex-1 md:ml-0">
              <h1 className="truncate text-lg font-semibold text-gray-900 dark:text-gray-100 md:text-xl">
                {navigation.find((item) => isCurrentPath(item.href))?.name ||
                  "Business Dashboard"}
              </h1>
            </div>

            {/* Right side - Theme toggle and Profile dropdown */}
            <div className="flex items-center space-x-3">
              {/* Theme Toggle */}
              <button
                className="flex items-center justify-center rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-300"
                onClick={toggleTheme}
                title={
                  isDarkMode ? "Switch to light mode" : "Switch to dark mode"
                }
              >
                {isDarkMode ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </button>

              {/* Profile Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  className="flex cursor-pointer items-center rounded-full p-2 text-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:hover:bg-gray-700"
                  onClick={() => {
                    setProfileDropdownOpen(!profileDropdownOpen);
                  }}
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
                    <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  </div>
                </button>

                {/* Dropdown Menu */}
                {profileDropdownOpen && (
                  <div className="absolute right-0 z-10 mt-2 min-w-48 max-w-xs origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800 dark:ring-gray-700">
                    <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {user?.firstName
                          ? `${user.firstName} ${user.lastName}`.trim()
                          : "Business Owner"}
                      </p>
                      <p className="break-words text-sm text-gray-500 dark:text-gray-400">
                        {user?.email}
                      </p>
                    </div>
                    <button
                      className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                      onClick={handleLogout}
                    >
                      <LogOut className="mr-3 h-4 w-4" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
            <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-gray-100">
              Confirm Logout
            </h3>
            <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
              Are you sure you want to sign out? You'll need to log in again to
              access your dashboard.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                onClick={cancelLogout}
              >
                Cancel
              </button>
              <button
                className="rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                onClick={confirmLogout}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessLayout;
